import React, { useMemo } from "react";
import Lanetalklogo from "shared/images/lanetalk_logo.svg";
import styles from "./cardCompacted.module.scss";

const StrikeOrSpare = ({ score, color, isTenthFrame }) => {
  if (score === "X")
    return (
      <span
        className={styles.strike}
        style={{
          width: isTenthFrame ? "50%" : "100%",
        }}
      >
        <svg
          width="21"
          height="17"
          viewBox="0 0 21 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.5"
            y="0.5"
            width="20"
            height="16"
            rx="3"
            fill={color || "#000"}
          />
          <path
            d="M13.5 5.5L7.5 11.5M7.5 5.5L13.5 11.5"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  if (score === "/")
    return (
      <span className={styles.spare}>
        <svg width="11" height="14" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.785.333h.77a1 1 0 0 1 1 1v11.334a1 1 0 0 1-1 1H.978a1 1 0 0 1-.823-1.568L7.96.766a1 1 0 0 1 .824-.433Z"
            fill={color || "#000"}
          />
        </svg>
      </span>
    );
  return (
    <span
      className={styles.throw}
      style={isTenthFrame ? { border: "none" } : {}}
    >
      {score}
    </span>
  );
};

const RenderScore = ({ throws, score, index, color }) => {
  // we apply this logic when there is no score to show yet
  if (!score) {
    // for the second throw of the frame (most certain the 10th frame)
    if (!!throws[index * 2 + 1]) {
      return (
        <span>
          <StrikeOrSpare
            score={throws[index * 2]}
            isTenthFrame={throws.length >= 19}
            color={color}
          />
          <StrikeOrSpare
            score={throws[index * 2 + 1]}
            isTenthFrame={throws.length >= 19}
            color={color}
          />
        </span>
      );
    }
    // for the first throw of the frame
    if (!!throws[index * 2]) {
      return (
        <StrikeOrSpare
          score={throws[index * 2]}
          isTenthFrame={throws.length >= 19}
          color={color}
        />
      );
    }
  }
  return <span>{score}</span>;
};

const CompactedScoreCard = ({ players, theme }) => {
  // get the player with the most frames
  const playerMostThrows = Array.from(players).reduce(
    (prevPlayer, currentPlayer) =>
      // check if current player has more throws than previous player
      currentPlayer[1].throws.length > prevPlayer.length
        ? currentPlayer[1].throws
        : prevPlayer,
    [],
  );

  // calculate the number of frames to display
  const framesOrigin = Array.from(
    { length: Math.min(Math.ceil(playerMostThrows.length / 2), 10) },
    (_, index) => index,
  );

  const frames =
    framesOrigin.length < 5
      ? Array.from({ length: 4 }, (_, index) => index)
      : framesOrigin.slice(-3);

  // Theme Style Declarations
  const playerNameTheme = useMemo(
    () => ({
      color: theme.player_name.font_color,
      fontFamily: `${theme.player_name.font_family}, sans-serif`,
      fontSize: theme.player_name.font_size,
      fontWeight: theme.player_name.font_bold ? "bold" : "normal",
      fontStyle: theme.player_name.font_style,
      backgroundColor: theme.section_container_background,
    }),
    [theme],
  );

  const totalScoreTheme = useMemo(
    () => ({
      color: theme.total_score.color,
      fontFamily: `${theme.total_score.font_family}, sans-serif`,
      fontSize: theme.total_score.font_size,
      fontWeight: theme.total_score.font_weight,
      backgroundColor: theme.total_score.background_color,
    }),
    [theme],
  );
  const headerTheme = useMemo(
    () => ({
      color: theme.game_label.font_color,
      fontSize: `calc(${theme.player_name.font_size} - 2px)`,
      fontFamily: `${theme.player_name.font_family}, sans-serif`,
      backgroundColor: theme.section_container_background,
    }),
    [theme],
  );

  const frameScoreTheme = useMemo(
    () => ({
      color: theme.frame_score.font_color,
      fontSize: theme.frame_score.font_size,
      fontWeight: theme.frame_score.font_bold ? "bold" : "normal",
      backgroundColor: theme.scores_container_background,
      fontFamily: `${theme.font_family}, sans-serif`,
    }),
    [theme],
  );

  return (
    <table
      className={styles.container}
      style={{
        backgroundColor: theme.lane_background,
      }}
    >
      <thead>
        <tr style={headerTheme}>
          <th className={styles.logo}>
            <img src={Lanetalklogo} alt="Lanetalk logo" />
          </th>
          {frames.map((frame, index) => (
            <th key={index}>{frame + 1}</th>
          ))}
          <th style={{ minWidth: 60 }}>max</th>
        </tr>
      </thead>
      <tbody>
        {Array.from(players).map(([key, player]) => (
          <tr key={key}>
            <td style={playerNameTheme} className={styles.playerName}>
              {player.playerName}
            </td>
            {frames.map((pos) => (
              <td
                key={`score-${pos}`}
                style={frameScoreTheme}
                className={styles.score}
              >
                <RenderScore
                  score={player.scores[pos]}
                  throws={player.throws}
                  index={pos}
                  color={theme.frame_score.font_color}
                />
              </td>
            ))}
            <td style={totalScoreTheme}>{player.maxScore}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CompactedScoreCard;
